<template>
  <v-container fluid class="pa-6">
    <v-row justify="center">
      <v-col cols="12" lg="12" xl="8">
        <v-card elevation="4" class="rounded-lg">
          <v-card-title class="blue-grey-lighten-4">
            <v-icon icon="mdi-receipt-text-outline" /> Refund Pay Voucher {{ isEntryMode ? "Entry" : "Update" }}
          </v-card-title>

          <v-card-text class="pa-6">
            <v-form ref="voucherForm" @submit.prevent="submit">
              <!-- Voucher Header Section -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    v-model="voucher_date.value.value"
                    label="Voucher Date *"
                    variant="outlined"
                    density="comfortable"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                  ></v-date-input>
                </v-col>

                <v-col cols="12" md="3">
                  <v-select
                    v-model="payment_method.value.value"
                    :items="paymentMethods"
                    item-title="text"
                    item-value="value"
                    label="Payment Method"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-cash-multiple"
                  
                      :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-select>
                </v-col>

                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="payment_acc_account_head_id.value.value"
                    :items="getPaymentAccountHeadList"
                    :item-title="(item) => `${item.name ?? 'Select'}`"
                    item-value="id"
                    label="Payment Account Head"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-bank"
                    clearable
                  
                      :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                        variant="outlined"
                        v-model="party.value.value"
                        label="Select Payee"
                        density="comfortable"
                        bg-color="grey-lighten-4"
                        :loading="loading"
                        :items="getPartyList"
                        :item-title="(item) => `${item.name ?? 'N/A'} - ${item.code ?? 'N/A'} - (${item.type ?? 'N/A'})`"
                        :error-messages="party.errorMessage.value"
                        prepend-icon=""
                        prepend-inner-icon="mdi-account"
                        item-value="id"
                        clearable
                      
                      :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                </v-col>
              </v-row>

              <!-- Bank Details Section (Conditional) -->
              <v-expand-transition>
                <div v-if="payment_method.value.value === 'BANK' && payment_acc_account_head_id.value.value !== null">
                  <v-divider class="my-4"></v-divider>

                  <div class="d-flex align-center mb-3">
                    <h3 class="text-h6 font-weight-medium">Bank Details</h3>
                  </div>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="selectChequeMethod"
                        :items="getChequeMethod"
                        item-title="text"
                        item-value="value"
                        label="Cheque Method"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-cash-multiple"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-select>
                    </v-col>

                    <v-col cols="12" md="4" v-if="selectChequeMethod === ''">
                      <v-select
                        v-model="cheque_id.value.value"
                        :items="getChequeList"
                        :item-title="(item) => item.cheque_no"
                        item-value="id"
                        label="Select Cheque No."
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-cash-multiple"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-select>
                    </v-col>

                    <v-col cols="12" md="4" v-else-if="selectChequeMethod === 'enter-cheque'">
                      <v-text-field
                        v-model="cheque_no.value.value"
                        variant="outlined"
                        density="comfortable"
                        label="Cheque Number"
                        hide-details
                        class="text-right"
                        prepend-inner-icon="mdi-cash-multiple"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="cheque_date.value.value"
                        label="Cheque Date"
                        variant="outlined"
                        density="comfortable"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                      ></v-date-input>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>

              <!-- Account Details Section -->
              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center mb-3">
                <h3 class="text-h6 font-weight-medium">Account Details</h3>
              </div>

              <!-- Account Details Table -->
              <v-table density="compact" class="voucher-table">
                <thead>
                  <tr>
                    <th class="text-left" width="20%">Account Head</th>
                    <th class="text-left" width="20%">Cost Center</th>
                    <th class="text-right" width="15%">Debit Amount</th>
                    <th class="text-right" width="15%">Credit Amount</th>
                    <th class="text-left" width="20%">Remarks</th>
                    <th class="text-center" width="10%">Action</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(row, index) in accountRows" :key="index">
                    <td class="py-2">
                      <v-autocomplete
                        variant="outlined"
                        label="Select Account Head"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="row.acc_account_head_id"
                        :items="getNonCashAccoutHeadList"
                        :item-title="(item) => `${item.name ?? 'Select'}`"
                        item-value="id"
                        autocomplete="off"
                        clearable
                        hide-details
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                    </td>

                    <td class="py-2">
                      <v-autocomplete
                        variant="outlined"
                        density="compact"
                        label="Cost Center"
                        bg-color="grey-lighten-4"
                        v-model="row.acc_cost_center_id"
                        :items="getConstCentersList"
                        :item-title="(item) => `${item.name ?? 'Select'} ${item.code ?? ''}`"
                        item-value="id"
                        autocomplete="off"
                        clearable
                        hide-details
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                    </td>

                    <td class="py-2">
                      <v-text-field
                        v-model="row.debit"
                        type="number"
                        min="0"
                        step="0.01"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :readonly="row.credit > 0"
                        @input="onDebitInput(index)"
                        class="text-right"
                      ></v-text-field>
                    </td>

                    <td class="py-2">
                      <v-text-field
                        v-model="row.credit"
                        type="number"
                        min="0"
                        step="0.01"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :readonly="row.debit > 0"
                        @input="onCreditInput(index)"
                        class="text-right"
                      ></v-text-field>
                    </td>

                    <td class="py-2">
                      <v-text-field
                        v-model="row.remarks"
                        variant="outlined"
                        density="compact"
                        hide-details
                        maxlength="255"
                      ></v-text-field>
                    </td>

                    <td class="justify-center text-center py-2">
                      <v-btn
                        icon
                        size="small"
                        color="error"
                        variant="tonal"
                        @click="removeRow(index)"
                        :disabled="accountRows.length === 1"
                        class="mr-1"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        color="primary"
                        variant="tonal"
                        @click="addRow"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>

                <tfoot>
                  <tr class="bg-grey-lighten-3">
                    <td colspan="2" class="text-right font-weight-bold">
                      Total
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(totalDebit) }}
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(totalCredit) }}
                    </td>
                    <td colspan="2"></td>
                  </tr>

                  <tr class="bg-blue-lighten-4">
                    <td colspan="2" class="text-right font-weight-bold">
                      Net Receipt
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(netReceiptAmount) }}
                    </td>
                    <td></td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </v-table>

              <!-- Narration Section -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <v-textarea
                    v-model="narration.value.value"
                    label="Narration"
                    variant="outlined"
                    density="comfortable"
                    rows="3"
                    maxlength="255"
                    counter
                    placeholder="Enter narration (optional)"
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- Action Buttons -->
              <v-card-actions class="justify-end mt-4 pa-4">
                <v-btn
                  color="grey"
                  variant="flat"
                  type="button"
                  @click="submitForm(0)"
                  size="large"
                  :loading="loading"
                >
                  DRAFT
                </v-btn>

                <v-btn
                  color="primary"
                  variant="flat"
                  type="button"
                  @click="submitForm(1)"
                  size="large"
                  :loading="loading"
                >
                  POST
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from "vue";
import { toast } from "vue3-toastify";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "RefundPayVoucherEntry",
  data : () => ({
    loading: false,
    selectChequeMethod: '',
  }),

  watch: {
    selectChequeMethod(val) {
      const queryId = this.$route.query.id;

      if (queryId) return;
      this.cheque_no.value.value = '';
      this.cheque_id.value.value = null;
    },
    "payment_acc_account_head_id.value.value": {
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch("getChequeList", {
            acc_account_head_id: newVal,
          });
        }
      },
    },

    "cheque_id.value.value": {
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch("getChequeById", newVal).then((res) => {
            this.cheque_no.value.value = res[0].cheque_no;
          })
        }
      },
    },
  },
  created() {
    // if (this.$store.getters.getNonCashAccoutHeadList.length === 0) {
      this.$store.dispatch("getNonCashAccoutHeadList");
    // }

    // if (this.$store.getters.getConstCentersList.length === 0) {
      this.$store.dispatch("getConstCentersList");
    // }
    // if (this.$store.getters.getPartyList.length === 0) {
      this.loading = true;
      this.$store.dispatch("getPartyList").finally(() => {
        this.loading = false;
      });
    // }
  },

  computed: {
    getChequeMethod() {
      const method = [
        { text: "Select Cheque No.", value: "" },
        { text: "Enter Cheque No.", value: "enter-cheque" },
      ]
      return method;
    },
    getChequeList() {
      return this.$store.getters.getChequeList;
    },

    getNonCashAccoutHeadList() {
      return this.$store.getters.getNonCashAccoutHeadList;
    },

    getPaymentAccountHeadList() {
      return this.$store.getters.getPaymentAccountHeadList;
    },

    getConstCentersList() {
      return this.$store.getters.getConstCentersList;
    },
    // Add this to ensure the list is properly reactive
    paymentAccountHeadsLoaded() {
      return (
        this.getPaymentAccountHeadList &&
        this.getPaymentAccountHeadList.length > 0
      );
    },

    getPartyList() {
      return this.$store.getters.getPartyList || [];
    },
  },

  methods: {
    addRow() {
      this.accountRows.push({
        acc_account_head_id: null,
        acc_cost_center_id: null,
        debit: 0,
        credit: 0,
        remarks: "",
      });
    },

    removeRow(index) {
      if (this.accountRows.length > 1) this.accountRows.splice(index, 1);
    },

    onDebitInput(index) {
      const row = this.accountRows[index];
      if (parseFloat(row.debit) > 0) row.credit = 0;
    },

    onCreditInput(index) {
      const row = this.accountRows[index];
      if (parseFloat(row.credit) > 0) row.debit = 0;
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },
  },

  setup() {
    const {proxy} =  getCurrentInstance();
    const { handleSubmit, resetForm,setFieldValue } = useForm({
      validationSchema: yup.object({
        voucher_date: yup.date().required().label("V Date"),
        payment_method: yup.string().required().label("Payment Method"),
        //party: yup.number().typeError("Payee is required").required("Payee is required").integer("Payee ID must be an integer").label("Payee"),
      }),
    });

    const submitForm = (status) => {
      setFieldValue('status', status);
      submit();                       
    };

    // Form fields using useField
    const voucher_date = useField("voucher_date");
    const party = useField("party");
    const acc_voucher_master_id = useField("acc_voucher_master_id");
    const payment_method = useField("payment_method");
    const payment_acc_account_head_id = useField("payment_acc_account_head_id");
    const narration = useField("narration");
    const isEntryMode = ref(true);


    // Bank details fields
    const cheque_id = useField("cheque_id");
    const cheque_no = useField("cheque_no");
    const cheque_date = useField("cheque_date");

    // Payment methods
    const paymentMethods = [
      { text: "Cash", value: "CASH" },
      { text: "Bank", value: "BANK" },
      { text: "Mobile Banking", value: "MOBILE_BANKING" },
    ];

    const accountRows = ref([
      {
        acc_account_head_id: null,
        acc_cost_center_id: null,
        debit: 0,
        credit: 0,
        remarks: "",
      },
    ]);

    const loading = ref(false);
    const store = useStore();
    const router = useRouter();

    // Watch for payment method changes and fetch corresponding account heads
    watch(
      () => payment_method.value.value,
      (newPaymentMethod) => {
        if (newPaymentMethod) {
          // Dispatch with proper payload structure
          store.dispatch("getPaymentAccountHeadList", { type: newPaymentMethod });
          // Reset payment account head when payment method changes
          payment_acc_account_head_id.value.value = null;
        }
      },
      { immediate: false } // Don't run on initial mount
    );

    watch(
      () => router.currentRoute.value.query.id,
      (newId, oldId) => {
        if (!newId) {
          resetForm();
          accountRows.value = [
            {
              acc_account_head_id: null,
              acc_cost_center_id: null,
              debit: 0,
              credit: 0,
              remarks: "",
            },
          ];
        } else {
          // console.log('📥 Loading voucher:', newId);
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );

    const totalDebit = computed(() =>
      accountRows.value.reduce(
        (sum, row) => sum + (parseFloat(row.debit) || 0),
        0
      )
    );

    
    const totalCredit = computed(() =>
      accountRows.value.reduce(
        (sum, row) => sum + (parseFloat(row.credit) || 0),
        0
      )
    );
    const netReceiptAmount = computed(() => {
      return totalDebit.value - totalCredit.value;
    });

    const submit = handleSubmit((values) => {
      loading.value = true;

      // Validate bank details if payment method is BANK
      if (payment_method.value.value === "BANK") {
        if (!cheque_no.value.value || !cheque_date.value.value) {
          toast.error("Please fill in bank details for bank payment");
          loading.value = false;
          return;
        }
      }

      // Format accounts with required fields
      const formattedAccounts = accountRows.value
        .filter((row) => row.acc_account_head_id)
        .map((row) => ({
          acc_account_head_id: row.acc_account_head_id,
          acc_cost_center_id: row.acc_cost_center_id || null,
          transaction_type: parseFloat(row.debit) > 0 ? "DEBIT" : "CREDIT",
          amount:
            parseFloat(row.debit) > 0
              ? parseFloat(row.debit)
              : parseFloat(row.credit),
          debit_amount: parseFloat(row.debit) || 0,
          credit_amount: parseFloat(row.credit) || 0,
          remarks: row.remarks || null,
        }));

      // Check if we have valid accounts
      if (formattedAccounts.length === 0) {
        toast.error("Please add at least one account entry");
        loading.value = false;
        return;
      }

      const payload = {
        type: "refund-pay-voucher",
        acc_voucher_master_id: acc_voucher_master_id.value.value,
        voucher_date: formattedDate(voucher_date.value.value),
        party_id: party.value.value,
        payment_method: payment_method.value.value,
        payment_acc_account_head_id: payment_acc_account_head_id.value.value,
        narration: narration.value.value || "",
        total_debit: totalDebit.value,
        total_credit: totalCredit.value,
        status: values.status,

        accounts: formattedAccounts,
        // conditional spreding based on payment method
        ...(payment_method.value.value === "BANK"
          ? {
              cheque_id: cheque_id.value.value,
              cheque_no: cheque_no.value.value,
              cheque_date: cheque_date.value.value,
            }
          : {
              bank_details: null,
            }),
      };

      // Add bank details based on payment method and cheque method
      if (payment_method.value.value === "BANK") {
        if (proxy.selectChequeMethod === '') {
          // Dropdown selection - send cheque_id
          payload.cheque_id = cheque_id.value.value;
          payload.cheque_no = cheque_no.value.value;
          payload.cheque_date = cheque_date.value.value;
        } else if (proxy.selectChequeMethod === 'enter-cheque') {
          // Manual entry - send cheque_no
          payload.cheque_id = null;
          payload.cheque_no = cheque_no.value.value;
          payload.cheque_date = cheque_date.value.value;
        }
      } else {
        payload.cheque_id = null;
        payload.cheque_no = null;
        payload.cheque_date = null;
      }

      store.dispatch("saveUpdateVoucher", payload)
        .then((response) => {
          resetForm();
          loading.value = false;
          toast.success(response.data.message, {
            timeout: 5000,
          });
          accountRows.value = [
            {
              acc_account_head_id: null,
              acc_cost_center_id: null,
              debit: 0,
              credit: 0,
              remarks: "",
            },
          ];
          router.push({ name: "refund-pay-voucher-record" });
        })
        .catch((error) => {
          toast.error(
            error.response?.data?.message || "Failed to save voucher"
          );
        })
        .finally(() => {
          loading.value = false;
        });
    });

    return {
      submit,
      submitForm,
      voucher_date,
      party,
      payment_method,
      acc_voucher_master_id,
      payment_acc_account_head_id,
      narration,
      cheque_id,
      cheque_no,
      cheque_date,
      paymentMethods,
      accountRows,
      totalDebit,
      totalCredit,
      netReceiptAmount,
      loading,
      isEntryMode
    };
  },

  mounted() {
    const queryId = this.$route.query.id;

    // If editing an existing voucher
    if (this.$store.getters.getEditingVoucher && queryId) {
      const voucher = this.$store.getters.getEditingVoucher;
      console.log(voucher);

      this.isEntryMode = false;

      // ✅ Set voucher header fields
      this.acc_voucher_master_id.value.value = voucher?.id;
      this.voucher_date.value.value = voucher?.voucher_date || "";
      this.party.value.value = voucher?.party_id || null;
      this.narration.value.value = voucher?.remarks || "";
      this.payment_method.value.value = voucher?.payment_method || "";

      // ✅ Fix: Ensure payment account heads are loaded before setting the value
      if (voucher?.payment_method) {
        this.$store.dispatch("getPaymentAccountHeadList", {
            type: voucher.payment_method,
          })
          .then(() => {
            // Set the value after the account heads are loaded
            this.payment_acc_account_head_id.value.value =
              voucher?.payment_acc_account_head_id || null;
          });
      } else {
        this.payment_acc_account_head_id.value.value =
          voucher?.payment_acc_account_head_id || null;
      }

      if(voucher?.payment_method === 'BANK') {
        if (voucher?.cheque_id) {
          this.selectChequeMethod = '';
          this.cheque_id.value.value = voucher?.cheque_id || null;
        } else {
          this.selectChequeMethod = 'enter-cheque';
          this.cheque_no.value.value = voucher?.cheque_no || '';
          this.cheque_id.value.value = null;
        }
      }
      this.cheque_date.value.value = voucher?.cheque_date || "";

      // ✅ Populate table rows from voucher_items
      if (voucher?.voucher_items && Array.isArray(voucher.voucher_items)) {
        this.accountRows = voucher.voucher_items.map((item) => ({
          acc_account_head_id: item.acc_account_head_id || null,
          acc_cost_center_id: item.acc_cost_center_id || null,
          debit:
            item.transaction_type === "DEBIT" ? parseFloat(item.amount) : 0,
          credit:
            item.transaction_type === "CREDIT" ? parseFloat(item.amount) : 0,
          remarks: item.remarks || "",
        }));
      }
    } else {
      this.isEntryMode = true;
      this.voucher_date.value.value = new Date();
    }
  },
};
</script>

<style scoped>
.voucher-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.voucher-table :deep(thead tr th) {
  background-color: #f5f5f5;
  font-weight: 600;
  padding: 12px 8px;
}

.voucher-table :deep(tbody tr td) {
  padding: 4px 8px;
}

.voucher-table :deep(tfoot tr td) {
  padding: 12px 8px;
}

.voucher-table :deep(.v-text-field input) {
  text-align: right;
}

.voucher-table :deep(.v-text-field input[readonly]) {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.voucher-table :deep(input[type="number"]::-webkit-inner-spin-button),
.voucher-table :deep(input[type="number"]::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
</style>